/**
 * WorkforceDraftReview — Aperture Bank interaction atlas §2 (D1–D5), stage 2 of
 * the atlas port. The human gate between the central optimiser and a published
 * branch schedule: the manager's only two real powers are revert and publish;
 * everything else on the screen is explanation.
 *
 * Ops: getScheduleDraft / finalizeScheduleDraft (REQ-037).
 *
 * Held pending the data layer (REQ-098): the draft's quality label ("Balanced"),
 * the rules version behind it, and the unchanged-shift count.
 */
import { useState } from "react";
import { Btn, Icon, Pill, T, alpha } from "@v0y4ge/jrni-ui";
import { Screen } from "../../shell/chrome";
import { DataSourceStrip } from "../../shell/opSource";
import { toast } from "../../shell/toast";
import { useViewport } from "../../shell/useResponsive";
import { useWfResource, useBusy } from "./useWorkforce";
import { getScheduleDraft, finalizeScheduleDraft, type ScheduleDraft, type DraftChange } from "./wf-api";
import { WfPending, dayLabel } from "./wf-shared";
import { useT } from "../../lib/i18n";

const KIND_TONE: Record<DraftChange["kind"], "success" | "warn" | "primary" | "info"> = {
  "New shift": "success", Removed: "warn", "Skill block": "primary", "Time change": "info",
};
const initials = (name: string) => name.split(" ").map((w) => w[0]).join("").slice(0, 2);

/**
 * One proposed change. Two controls only — Why? and Revert — as in the capture;
 * revert is a toggle, so a misclick is undone by clicking Restore proposal.
 * Reverted means "keep the current shift", not "delete the change", which is why
 * the proposal stays on screen struck through rather than disappearing.
 */
function ChangeRow({ c, reverted, locked, onToggle }: { c: DraftChange; reverted: boolean; locked: boolean; onToggle: () => void }) {
  const t = useT();
  const phone = useViewport() === "phone";
  const [open, setOpen] = useState(false); // ui-local
  return (
    <div style={{ borderTop: `1px solid ${T.n6}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 13, padding: "12px 20px", flexWrap: "wrap" }}>
        <span style={{ width: 28, height: 28, borderRadius: "50%", background: T.p3, color: T.p1, fontSize: 10, fontWeight: 700, display: "grid", placeItems: "center", flexShrink: 0 }}>{initials(c.advisor)}</span>
        <span style={{ display: "flex", flexDirection: "column", width: 132, flexShrink: 0 }}>
          <b style={{ fontSize: 12.5, color: T.n1 }}>{c.advisor}</b>
          <span style={{ fontSize: 10.5, color: T.soft }}>{c.day}</span>
        </span>
        <span style={{ width: 92, flexShrink: 0 }}><Pill tone={KIND_TONE[c.kind]}>{c.kind}</Pill></span>
        <span style={{ display: "flex", gap: 8, alignItems: "center", flex: 1, minWidth: 200, fontFamily: T.fontMono, fontSize: 11.5 }}>
          <span style={{ color: T.soft, textDecoration: "line-through" }}>{c.from}</span>
          <span style={{ color: T.soft }}>→</span>
          <span style={{ fontWeight: 700, color: reverted ? T.soft : T.n1, textDecoration: reverted ? "line-through" : "none" }}>{c.to}</span>
          {reverted ? <span style={{ fontFamily: T.font, fontSize: 11, color: T.soft }}>{t("workforce.draftReview.keepingCurrent", "Keeping current")}</span> : null}
        </span>
        <span style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0, marginLeft: phone ? 0 : "auto" }}>
          <Btn size="sm" variant="link" testid="wfdraft-why" onClick={() => setOpen((v) => !v)}>
            {open ? t("workforce.workforceDraftReview.hideWhy", "Hide why") : t("workforce.workforceDraftReview.why", "Why?")}
          </Btn>
          <Btn size="sm" variant={reverted ? "primary" : "secondary"} testid="wfdraft-revert" disabled={locked} onClick={onToggle}>
            {reverted ? t("workforce.workforceDraftReview.restoreProposal", "Restore proposal") : t("settings.branding.revert", "Revert")}
          </Btn>
        </span>
      </div>
      {open ? (
        <div style={{ padding: "0 20px 12px 61px" }}>
          <div style={{ padding: "10px 13px", background: T.p4, borderRadius: 7, fontSize: 12, color: T.n2, lineHeight: 1.55 }}>
            <b style={{ color: T.p1 }}>{t("workforce.draftReview.optimiser", "Optimiser:")} </b>{c.why}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function WorkforceDraftReview() {
  const t = useT();
  const phone = useViewport() === "phone";
  const { data, loading, error, reload } = useWfResource<ScheduleDraft>("draft", getScheduleDraft);
  const { busy, run } = useBusy();
  const [reverted, setReverted] = useState<Set<string>>(new Set()); // ui-local, pre-finalize
  const locked = data?.status !== "in-review";

  const finalize = (action: "publish" | "escalate") => void run(action, async () => {
    try {
      await finalizeScheduleDraft({ action, revertedChangeIds: [...reverted] });
      toast({
        tone: "success",
        title: action === "publish" ? t("workforce.draftReview.branchSchedulePublished", "Branch schedule published") : t("workforce.draftReview.escalatedToWorkforceAdmin", "Escalated to Workforce admin"),
        subtitle: action === "publish" ? t("workforce.draftReview.noticeClockStartedAdvisorsNotified", "Notice clock started · advisors notified") : t("workforce.draftReview.draftLockedUntilResolved", "Draft locked until resolved"),
      });
      reload();
    } catch (e) { toast({ tone: "danger", title: t("workforce.draftReview.couldnTFinalizeTheDraft", "Couldn't finalize the draft"), subtitle: e instanceof Error ? e.message : String(e) }); }
  });

  const revertedChanges = data ? data.changes.filter((c) => reverted.has(c.id)) : [];
  const hardBlock = revertedChanges.some((c) => c.kind === "Removed");

  /** Atlas D5: the consequence names the change and says it is legal AND logged. */
  const fairnessLine = () => {
    const names = revertedChanges.map((c) => c.advisor.split(" ")[0]);
    const who = names.length === 1 ? names[0] : names.length === 2 ? `${names[0]} ↔ ${names[1]}` : `${names.length} ${t("workforce.draftReview.changes", "changes")}`;
    return `${t("workforce.draftReview.reverting", "Reverting the")} ${who} ${t("workforce.draftReview.swapWidensSpread", "swap widens the close-rotation spread (legal, but logged against fairness).")}`;
  };

  const stat = (label: string, value: string, tone?: string) => (
    <span key={label} style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 74 }}>
      <span style={{ fontSize: 9.5, color: T.soft, textTransform: "uppercase", letterSpacing: 0.07, fontWeight: 700 }}>{label}</span>
      <span style={{ fontSize: 18, fontWeight: 700, color: tone ?? T.n1, fontFamily: T.fontMono, lineHeight: 1.1 }}>{value}</span>
    </span>
  );

  return (
    <Screen rail="wf-draft">
      <DataSourceStrip screen="wfdraft" ops={["getScheduleDraft", "finalizeScheduleDraft"]} reqId="REQ-037" />
      <div style={{ flex: 1, overflow: "auto", padding: "14px 24px 32px", display: "flex", flexDirection: "column", gap: 14 }}>
        {error ? <WfPending screen="wfdraft" opName="getScheduleDraft" error={error} onRetry={reload} />
          : loading || !data ? <div style={{ padding: 40, textAlign: "center", color: T.soft, fontSize: 14 }}>{t("common.status.loading", "Loading…")}</div>
          : <>
            {/* D1 — provenance header. "Generated by … rules v13" is why a manager
                trusts or challenges the draft; it is not decoration. */}
            <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, background: T.white, padding: "15px 20px", display: "flex", justifyContent: "space-between", gap: 20, flexWrap: "wrap", flexShrink: 0 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 5, minWidth: 260, flex: 1 }}>
                <span style={{ display: "flex", gap: 9, alignItems: "center", flexWrap: "wrap" }}>
                  <b style={{ fontSize: 14, color: T.n1 }}>{t("workforce.draftReview.centralDraft2", "Central draft")} · {data.weekLabel}</b>
                  <span style={{ fontSize: 11.5, color: T.soft, fontFamily: T.fontMono }}>{t("workforce.draftReview.run", "Run")}{" "}{data.runId}</span>
                  <Pill tone={data.status === "published" ? "success" : data.status === "escalated" ? "warn" : "primary"}>
                    {data.status === "published" ? t("workforce.draftReview.published", "Published") : data.status === "escalated" ? t("workforce.draftReview.escalated", "Escalated") : t("workforce.draftReview.inReview", "In review")}
                  </Pill>
                </span>
                <span style={{ fontSize: 12, color: T.n2, lineHeight: 1.55, maxWidth: 560 }}>
                  {t("workforce.draftReview.generatedBy", "Generated by")}{" "}{data.generatedBy} {t("workforce.draftReview.at", "at")}{" "}{new Date(data.generatedAt).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })} · {t("workforce.draftReview.reviewDue2", "review due")}{" "}<b style={{ color: T.n1 }}>{dayLabel(data.reviewDue, "full")}</b>. {t("workforce.draftReview.adjustWithinGuardrails", "You can adjust within guardrails — every edit re-validates live.")}
                </span>
              </div>
              <div style={{ display: "flex", gap: 22, flexWrap: "wrap", alignItems: "flex-start" }}>
                {stat(t("workforce.draftReview.changesLabel", "Changes"), String(data.changes.length))}
                {stat(t("workforce.draftReview.advisors", "Advisors"), String(data.advisorsCount))}
                {stat(t("workforce.draftReview.costDelta", "Cost Δ"), data.costDeltaLabel, T.green1)}
                {stat(t("workforce.draftReview.coverageAfter", "Coverage after"), `${data.coveragePct}%`)}
              </div>
            </div>

            {/* D2 — the diff. The "unchanged shifts are not shown" caption is
                load-bearing: this is a diff view, not the schedule. */}
            <div style={{ border: `1px solid ${T.border}`, borderRadius: 12, background: T.white, overflow: "hidden", flexShrink: 0 }}>
              <div style={{ padding: "15px 20px 13px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                <span style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <b style={{ fontSize: 14.5, color: T.n1 }}>{t("workforce.draftReview.proposedChanges", "Proposed changes")}</b>
                  <span style={{ fontSize: 11.5, color: T.soft }}>{t("workforce.draftReview.diffCaption", "Current → proposed · unchanged shifts are not shown · expand any row for the optimiser's reasoning")}</span>
                </span>
                <span style={{ fontSize: 11.5, color: T.soft, whiteSpace: "nowrap" }}>{reverted.size} {t("workforce.draftReview.reverted", "reverted")}</span>
              </div>
              {data.changes.map((c) => (
                <ChangeRow key={c.id} c={c} reverted={reverted.has(c.id)} locked={locked}
                  onToggle={() => setReverted((s) => { const n = new Set(s); if (n.has(c.id)) n.delete(c.id); else n.add(c.id); return n; })} />
              ))}
            </div>

            {hardBlock ? (
              <div style={{ display: "flex", gap: 10, alignItems: "center", padding: "11px 15px", borderRadius: 8, background: alpha(T.red1, 8) }}>
                <Icon.warn size={14} style={{ color: T.red1, flexShrink: 0 }} />
                <span style={{ fontSize: 12.5, fontWeight: 600, color: T.n1 }}>{t("workforce.draftReview.restoringARemovedShiftMayReintroduceAHardRuleViolationPublishIs", "Restoring a removed shift may reintroduce a hard rule violation. Publish is disabled until resolved.")}</span>
              </div>
            ) : reverted.size > 0 ? (
              <div data-testid="wfdraft-fairness" style={{ display: "flex", gap: 10, alignItems: "center", padding: "11px 15px", borderRadius: 8, background: alpha(T.orange1, 10) }}>
                <Icon.warn size={14} style={{ color: T.orange1, flexShrink: 0 }} />
                <span style={{ fontSize: 12.5, fontWeight: 600, color: T.n1 }}>{fairnessLine()}</span>
              </div>
            ) : (
              <div style={{ display: "flex", gap: 10, alignItems: "center", padding: "11px 15px", borderRadius: 8, background: alpha(T.green1, 10) }}>
                <Icon.check size={14} style={{ color: T.green1, flexShrink: 0 }} />
                <span style={{ fontSize: 12.5, fontWeight: 600, color: T.n1 }}>
                  {t("workforce.draftReview.allChecksPass", "All checks pass")} · {t("workforce.draftReview.budget", "budget")}{" "}{data.budgetLabel} · {t("workforce.draftReview.noticeLower", "notice")}{" "}{data.noticeDaysLabel} · {t("workforce.draftReview.noRuleViolations2", "no rule violations")}
                </span>
              </div>
            )}

            {/* D2 — publish names all three irreversible effects. Escalate is a
                first-class outcome, not an error path. */}
            <div style={{ border: `1px solid ${T.border}`, borderRadius: 12, background: T.white, padding: "15px 20px", display: "flex", justifyContent: "space-between", gap: 14, flexWrap: "wrap", alignItems: "center", flexShrink: 0 }}>
              <span style={{ fontSize: 12, color: T.soft, maxWidth: 470, lineHeight: 1.5 }}>
                {t("workforce.draftReview.publishingStarts", "Publishing starts the advance-notice clock, notifies")}{" "}{data.advisorsCount} {t("workforce.draftReview.advisorsAndUpdates", "advisors, and updates jrni bookable availability. Escalating locks the draft and sends it back to the Workforce admin with your notes.")}
              </span>
              <span style={{ display: "flex", gap: 8, flexWrap: "wrap", width: phone ? "100%" : "auto" }}>
                <Btn variant="secondary" testid="wfdraft-escalate" disabled={locked || busy !== null} onClick={() => finalize("escalate")}>
                  {busy === "escalate" ? "…" : t("workforce.workforceDraftReview.escalateToAdmin", "Escalate to admin")}
                </Btn>
                <Btn testid="wfdraft-publish" disabled={hardBlock || locked || busy !== null} onClick={() => finalize("publish")}>
                  <Icon.network size={13} /> {busy === "publish" ? "…" : t("workforce.workforceDraftReview.publishBranchSchedule", "Publish branch schedule")}
                </Btn>
              </span>
            </div>
          </>}
      </div>
    </Screen>
  );
}
